import React, { useMemo, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, AgGridReact, Grid } from "@jsq/ds/core";
import { PayeeDetails } from "../PayeeDetails";
import { columnDefinitions } from "./columnDefinitions";
import { PayeesGraphqlDataSource } from "./payeesGQLDataSource";
import { sideBarDefs } from "./sidebarFactory";
import { tableContainer } from "./styles_vanilla.css";
import { PayeeRowItem } from "./types";

const rowCountPerPage = 20;

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  pagination: true,
  paginationPageSize: rowCountPerPage,
  paginationPageSizeSelector: false,
  rowHeight: 50,
  suppressCellFocus: true,
  suppressContextMenu: true,
};

const defaultColumnOptions = {
  editable: false,
  flex: 1,
  minWidth: 100,
  sortable: false,
};

export interface PayeesTableProps {
  /**
   * The selected payee ID (global ID string).
   * When provided, the slideout opens to show this payee's details.
   */
  selectedPayeeId?: string;
  /**
   * Callback fired when a table row is clicked.
   * The consumer should update the URL to reflect the selected payee.
   */
  onSelectPayee?: (payeeId: string) => void;
  /**
   * Callback fired when the slideout close button is clicked.
   * The consumer should navigate back to clear the selection from the URL.
   */
  onCloseSlideout?: () => void;
}

export const PayeesTable: React.FC<PayeesTableProps> = ({
  selectedPayeeId,
  onSelectPayee,
  onCloseSlideout,
}) => {
  const client = useApolloClient();

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new PayeesGraphqlDataSource(client, rowCountPerPage);
  }, [client]);

  const onRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<PayeeRowItem>) => {
      const payeeRowDetails = event.data;
      if (payeeRowDetails) {
        onSelectPayee?.(payeeRowDetails.id);
      }
    },
    [onSelectPayee],
  );

  return (
    <>
      <Grid<PayeeRowItem>
        className={tableContainer}
        cacheBlockSize={rowCountPerPage}
        columnDefs={columnDefinitions}
        defaultColDef={defaultColumnOptions}
        gridId="payees_table"
        gridOptions={{
          ...gridOptions,
        }}
        onRowClicked={onRowClicked}
        reactiveCustomComponents
        rowModelType="serverSide"
        serverSideDatasource={gridDataSource}
        sideBar={sideBarDefs()}
      />
      <PayeeDetails onClose={onCloseSlideout ?? (() => {})} payeeId={selectedPayeeId} />
    </>
  );
};

export default PayeesTable;
